import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  Upload,
  IndianRupee,
  ArrowLeft,
  MoreVertical,
  ChevronDown,
  Check,
  ChevronLeft,
  X,
  Loader,
} from "lucide-react";
import { BASE_URL } from "utils/constant";

const WorkerSelectionModal = ({ onClose, onSelectWorker }) => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkers = async () => {
      const site = localStorage.getItem("site");
      const accessToken = localStorage.getItem("access");
      try {
        const response = await fetch(`${BASE_URL}/worker/workers-page/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            worker_type: "Worker",
            sites: parseInt(site),
          }),
        });
        const data = await response.json();
        if (data.status) {
          setWorkers(data.data);
        }
      } catch (error) {
        console.error("Error fetching workers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Select Worker</h2>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">
            Loading...
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {workers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200 my-4">
                <p className="text-gray-600 mb-1">No workers found</p>
                <p className="text-gray-500">
                  Add new workers from the workers tab
                </p>
              </div>
            ) : (
              workers.map((worker) => (
                <button
                  key={worker.id}
                  className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100"
                  onClick={() => onSelectWorker(worker)}
                >
                  <div className="font-medium">{worker.worker_name}</div>
                  <div className="text-sm text-gray-500">
                    {worker.worker_workname} - {worker.worker_workcategory}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const WorkerPayment = ({ onClose }) => {
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedWorkerAttendance, setSelectedWorkerAttendance] =
    useState(null);
  const [selectedWorkerPayments, setSelectedWorkerPayments] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentType, setPaymentType] = useState("Wage");
  const [dateMode, setDateMode] = useState("Now");
  const [dateValue, setDateValue] = useState(() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  });

  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);
  const [workerTransactionCatagory, setWorkerTransactionCatagory] =
    useState(null);
  const paymentId = "WP_ID_01";

  // Multi-worker payment states
  const [isMultiPayment, setIsMultiPayment] = useState(false);
  const [allWorkers, setAllWorkers] = useState([]);
  const [defaulterWorkers, setDefaulterWorkers] = useState([]);
  const [loadingDefaulters, setLoadingDefaulters] = useState(false);

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Worker Transaction"
          );
          if (purchaseCatagory) {
            setWorkerTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  const getSelectedWorkerAttendance = () => {
    const accessToken = localStorage.getItem("access");
    try {
      fetch(
        `${BASE_URL}/worker/get-a-worker-attendance/${selectedWorker?.worker_id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            setSelectedWorkerAttendance(data.data);
          }
        });
    } catch (error) {
      console.error("Error fetching worker attendance:", error);
    }
  };

  const getSelectedWorkerPayments = () => {
    const accessToken = localStorage.getItem("access");
    try {
      fetch(`${BASE_URL}/finance/get-worker-transactions-of-a-worker/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          worker_id: selectedWorker.worker_id,
          sites: parseInt(localStorage.getItem("site")),
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            setSelectedWorkerPayments(data.data);
          }
        });
    } catch (error) {
      console.error("Error fetching worker payments:", error);
    }
  };

  useEffect(() => {
    if (selectedWorker) {
      getSelectedWorkerAttendance();
      getSelectedWorkerPayments();
    }
  }, [selectedWorker]);

  useEffect(() => {
    if (isMultiPayment) {
      fetchAllWorkersWithBalances();
    }
  }, [isMultiPayment]);

  const fetchAllWorkersWithBalances = async () => {
    setLoadingDefaulters(true);
    const site = localStorage.getItem("site");
    const accessToken = localStorage.getItem("access");

    try {
      const workersResponse = await fetch(`${BASE_URL}/worker/workers-page/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          worker_type: "Worker",
          sites: parseInt(site),
        }),
      });
      const workersData = await workersResponse.json();

      if (!workersData.status) {
        setLoadingDefaulters(false);
        return;
      }

      const workers = workersData.data;
      const workersWithBalances = [];

      for (const worker of workers) {
        try {
          const attendanceResponse = await fetch(
            `${BASE_URL}/worker/get-a-worker-attendance/${worker.worker_id}/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          const attendanceData = await attendanceResponse.json();

          const paymentsResponse = await fetch(
            `${BASE_URL}/finance/get-worker-transactions-of-a-worker/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                worker_id: worker.worker_id,
                sites: parseInt(site),
              }),
            }
          );
          const paymentsData = await paymentsResponse.json();

          let baseEarnings = 0;
          let overtimeEarnings = 0;

          if (attendanceData.status && attendanceData.data) {
            attendanceData.data.forEach((attendance) => {
              if (attendance.attendance === "Present") {
                const dailyWage = worker.daily_wage || 0;
                if (attendance.full_day) {
                  baseEarnings += dailyWage;
                } else if (attendance.half_day) {
                  baseEarnings += dailyWage / 2;
                }
                if (attendance.overtime_wage) {
                  overtimeEarnings += attendance.overtime_wage;
                }
              }
            });
          }

          const totalEarnings = Math.round(baseEarnings + overtimeEarnings);

          const totalPayments = paymentsData.status
            ? paymentsData.data.reduce(
                (sum, payment) => sum + (payment.amount || 0),
                0
              )
            : 0;

          const balance = totalEarnings - totalPayments;

          workersWithBalances.push({
            ...worker,
            totalEarnings,
            totalPayments,
            balance,
          });
        } catch (error) {
          console.error(`Error processing worker ${worker.worker_id}:`, error);
        }
      }

      const defaulters = workersWithBalances.filter(
        (worker) => worker.balance > 0
      );

      setAllWorkers(workersWithBalances);
      setDefaulterWorkers(defaulters);

      const totalPending = defaulters.reduce((sum, w) => sum + w.balance, 0);
      setAmount(totalPending.toString());
    } catch (error) {
      console.error("Error fetching workers with balances:", error);
    } finally {
      setLoadingDefaulters(false);
    }
  };

  const calculatedEarnings = useMemo(() => {
    if (!selectedWorkerAttendance || !selectedWorker) {
      return { totalEarnings: 0, baseEarnings: 0, overtimeEarnings: 0 };
    }

    let baseEarnings = 0;
    let overtimeEarnings = 0;
    let deductions = 0;
    selectedWorkerAttendance.forEach((attendance) => {
      if (attendance.attendance === "Present") {
        const dailyWage = selectedWorker.daily_wage || 0;
        baseEarnings += dailyWage;
        if (attendance.half_day) {
          deductions += dailyWage / 2;
        }

        if (attendance.overtime_wage) {
          overtimeEarnings += attendance.overtime_wage;
        }
      }
    });

    const totalEarnings = baseEarnings + overtimeEarnings - deductions;

    return {
      totalEarnings: Math.round(totalEarnings),
      baseEarnings: Math.round(baseEarnings),
      overtimeEarnings: Math.round(overtimeEarnings),
      deductions: Math.round(deductions),
    };
  }, [selectedWorkerAttendance, selectedWorker]);

  const totalPayment = useMemo(() => {
    return selectedWorkerPayments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
  }, [selectedWorkerPayments]);

  const paymentRemaining = useMemo(() => {
    return calculatedEarnings.totalEarnings - totalPayment;
  }, [calculatedEarnings, totalPayment]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setReceiptImage(file);
  };

  const handleSave = async () => {
    if (isMultiPayment) {
      await handleMultiWorkerPayment();
    } else {
      await handleSingleWorkerPayment();
    }
  };

  const handleSingleWorkerPayment = async () => {
    if (!selectedWorker || !amount || isLoading || isSaveClicked) return;

    setIsLoading(true);
    setIsSaveClicked(true);

    const site = localStorage.getItem("site");

    let formattedDate = dateValue;
    if (dateMode === "Now") {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      formattedDate = `${yyyy}-${mm}-${dd}`;
    }

    const formData = new FormData();
    formData.append("payment_type", paymentType);
    formData.append("amount", parseInt(amount));
    formData.append("worker_id", selectedWorker.worker_id || selectedWorker.id);
    formData.append("transaction_date", formattedDate);
    formData.append("transaction_method", paymentMethod);
    formData.append("comments", remarks);
    formData.append("transaction_catagory_id", workerTransactionCatagory);
    formData.append("site", parseInt(site));

    if (receiptImage) {
      formData.append("receipt_image", receiptImage);
    }

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-worker-transaction/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );
      const data = await response.json();
      if (data.status) {
        onClose();
      } else {
        setIsSaveClicked(false);
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      setIsSaveClicked(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiWorkerPayment = async () => {
    if (defaulterWorkers.length === 0 || !amount || isLoading || isSaveClicked)
      return;

    const totalPending = defaulterWorkers.reduce(
      (sum, w) => sum + w.balance,
      0
    );

    if (parseInt(amount) !== totalPending) {
      alert(
        `You must pay exactly ₹${totalPending.toLocaleString(
          "en-IN"
        )} for all defaulters.`
      );
      return;
    }

    setIsLoading(true);
    setIsSaveClicked(true);

    const site = localStorage.getItem("site");
    const accessToken = localStorage.getItem("access");

    let formattedDate = dateValue;
    if (dateMode === "Now") {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      formattedDate = `${yyyy}-${mm}-${dd}`;
    }

    try {
      const paymentPromises = defaulterWorkers.map(async (worker) => {
        const formData = new FormData();
        formData.append("payment_type", paymentType);
        formData.append("amount", worker.balance);
        formData.append("worker_id", worker.worker_id || worker.id);
        formData.append("transaction_date", formattedDate);
        formData.append("transaction_method", paymentMethod);
        formData.append(
          "comments",
          remarks || `Multi-worker payment - ${worker.worker_name}`
        );
        formData.append("transaction_catagory_id", workerTransactionCatagory);
        formData.append("site", parseInt(site));

        if (receiptImage) {
          formData.append("receipt_image", receiptImage);
        }

        return fetch(`${BASE_URL}/finance/create-worker-transaction/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });
      });

      const responses = await Promise.all(paymentPromises);
      const allSuccessful = responses.every((res) => res.ok);

      if (allSuccessful) {
        onClose();
      } else {
        alert("Some payments failed. Please check and try again.");
        setIsSaveClicked(false);
      }
    } catch (error) {
      console.error("Error creating multi-worker payments:", error);
      alert("Error processing payments. Please try again.");
      setIsSaveClicked(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full bg-gray-100 min-h-screen py-6 px-4">
      {showWorkerModal && (
        <WorkerSelectionModal
          onClose={() => setShowWorkerModal(false)}
          onSelectWorker={(worker) => {
            setSelectedWorker(worker);
            setShowWorkerModal(false);
          }}
        />
      )}

      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-6 pt-1">
        <button onClick={onClose} className="text-[#232323] hover:text-black">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <p className=" text-[#232323] ml-20 text-[20px] mt-1">Worker Payment</p>

        {/* Toggle for Multi-Worker Payment */}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-[14px] text-[#232323]">
            Multi-Worker Payment
          </span>
          <button
            onClick={() => {
              setIsMultiPayment(!isMultiPayment);
              setSelectedWorker(null);
              setAmount("");
            }}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              isMultiPayment ? "bg-[#3B0069]" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                isMultiPayment ? "transform translate-x-6" : ""
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex justify-between gap-12 border rounded-[12px] border-gray-300 p-6 bg-white shadow-sm">
        {/* Left */}
        <div className="w-1/2 space-y-[30px]">
          {/* Worker Profile */}
          <div className="flex items-center gap-3">
            <div className="text-[#232323] text-[14px]">
              Payment ID.
              <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                {paymentId}
              </span>
            </div>
          </div>

          {/* Conditional Rendering based on payment mode */}
          {!isMultiPayment ? (
            <>
              {/* Single Worker Selection */}
              <div>
                <label className="block text-[#000000] text-[16px] mb-1 font-medium">
                  Select Worker
                </label>
                <button
                  onClick={() => setShowWorkerModal(true)}
                  className="w-full px-4 py-2 rounded-md bg-white border outline-none text-[#232323] text-left flex items-center justify-between"
                >
                  <span>
                    {selectedWorker
                      ? selectedWorker.worker_name
                      : "Worker unassigned"}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              {selectedWorker && (
                <div className="rounded-lg p-4 border border-purple-200">
                  <p className="text-[#232323] text-[16px] font-semibold mb-3">
                    Payment Summary
                  </p>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[#232323] text-[14px]">
                        Total Earnings
                      </span>
                      <span className="text-[#3B0069] font-semibold text-[15px]">
                        ₹
                        {calculatedEarnings.totalEarnings.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between items-center pl-4 text-[13px]">
                      <span className="text-gray-600">Base Earnings</span>
                      <span className="text-gray-700">
                        ₹
                        {calculatedEarnings.baseEarnings.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>

                    {calculatedEarnings.overtimeEarnings > 0 && (
                      <div className="flex justify-between items-center pl-4 text-[13px]">
                        <span className="text-gray-600">Overtime Earnings</span>
                        <span className="text-gray-700">
                          ₹
                          {calculatedEarnings.overtimeEarnings.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                    )}
                    {calculatedEarnings.deductions > 0 && (
                      <div className="flex justify-between items-center pl-4 text-[13px]">
                        <span className="text-gray-600">Deductions</span>
                        <span className="text-gray-700">
                          - ₹
                          {calculatedEarnings.deductions.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                    )}

                    <hr className="my-2" />

                    <div className="flex justify-between items-center">
                      <span className="text-[#232323] text-[14px]">
                        Payment Made
                      </span>
                      <span className="text-red-600 font-semibold text-[15px]">
                        - ₹{totalPayment.toLocaleString("en-IN")}
                      </span>
                    </div>

                    <hr className="my-2 border-purple-300" />

                    <div className="flex justify-between items-center bg-white rounded-md p-2">
                      <span className="text-[#232323] text-[15px] font-semibold">
                        Payment Remaining
                      </span>
                      <span
                        className={`font-bold text-[16px] ${
                          paymentRemaining >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        ₹{Math.abs(paymentRemaining).toLocaleString("en-IN")}
                        {paymentRemaining < 0 && " (Overpaid)"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Multi-Worker Payment Summary */}
              <div className="rounded-lg p-4 border border-purple-200">
                <p className="text-[#232323] text-[16px] font-semibold mb-3">
                  Multi-Worker Payment Summary
                </p>

                {loadingDefaulters ? (
                  <div className="text-center py-4">
                    <Loader className="w-6 h-6 animate-spin mx-auto text-[#3B0069]" />
                    <p className="text-sm text-gray-600 mt-2">
                      Loading workers...
                    </p>
                  </div>
                ) : defaulterWorkers.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600">
                      No workers with pending payments
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-purple-50 p-2 rounded">
                      <span className="text-[#232323] text-[14px] font-semibold">
                        Total Defaulters
                      </span>
                      <span className="text-[#3B0069] font-bold text-[15px]">
                        {defaulterWorkers.length}
                      </span>
                    </div>

                    <div className="max-h-48 overflow-y-auto space-y-2 border rounded p-2">
                      {defaulterWorkers.map((worker) => (
                        <div
                          key={worker.id}
                          className="flex justify-between items-center text-[13px] py-1 border-b border-gray-100 last:border-0"
                        >
                          <span className="text-gray-700">
                            {worker.worker_name}
                          </span>
                          <span className="text-[#3B0069] font-semibold">
                            ₹{worker.balance.toLocaleString("en-IN")}
                          </span>
                        </div>
                      ))}
                    </div>

                    <hr className="my-2 border-purple-300" />

                    <div className="flex justify-between items-center bg-white rounded-md p-2">
                      <span className="text-[#232323] text-[15px] font-semibold">
                        Total Pending Amount
                      </span>
                      <span className="text-green-600 font-bold text-[16px]">
                        ₹
                        {defaulterWorkers
                          .reduce((sum, w) => sum + w.balance, 0)
                          .toLocaleString("en-IN")}
                      </span>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                      <p className="text-[12px] text-yellow-800">
                        ⚠️ You must pay the exact total amount. No partial
                        payments allowed.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {/* Select Payment Date */}
          <div className="mt-4">
            <label className="text-[15px] text-[#232323] mb-1 block">
              Select Payment Date
            </label>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => setDateMode("Now")}
                className="border-1 border-[#7F599D] h-[36px] bg-[#ECE6F0] text-[#3B0069] rounded-[12px] px-5 py-1 text-sm font-medium"
              >
                Now
              </button>
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  setDateValue(e.target.value);
                  setDateMode("Custom");
                }}
                className="border rounded-lg px-4 py-2 text-[16px] text-[#232323] w-full bg-white"
              />
            </div>
          </div>

          {/* Payment Section */}
          <div className="pt-2">
            <p className="text-[#232323] text-[18px] mb-2">Payment</p>
            <hr className="mb-4" />

            {/* Paid Amount */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-[#232323] text-[15px] whitespace-nowrap">
                Paid Amount
              </label>
              <div className="relative w-full">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="number"
                  placeholder="Amount"
                  className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-white"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={isMultiPayment}
                  title={
                    isMultiPayment
                      ? "Amount is fixed for multi-worker payment"
                      : ""
                  }
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="flex gap-2">
              <p>Payment Method</p>
              {["Cash", "Online", "Cheque"].map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`px-4 py-1 rounded-full text-sm font-medium ${
                    paymentMethod === method
                      ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right - Could add additional info or summary here */}
        <div className="w-1/2">
          {/* Remarks/Comments */}
          <div>
            <label className="text-[#232323] text-[15px] mb-1 block">
              Remarks/ Comments (Optional)
            </label>
            <textarea
              type="text"
              placeholder="Type"
              className="w-full border rounded-lg px-4 py-2 text-sm min-h-[60px] bg-white"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Payment Slip */}
          <div>
            <label className="text-[#232323] text-[15px] mb-1 block">
              Payment Slip (Optional)
            </label>
            <label className="w-full h-[36px] flex items-center justify-center px-4 py-8 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-gray-200">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
              />
              <span className="flex items-center gap-2">
                <Upload size={16} />
                {receiptImage ? receiptImage.name : "Upload Photos"}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}

      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSave}
          disabled={
            isLoading ||
            isSaveClicked ||
            (!selectedWorker && defaulterWorkers.length == 0) ||
            !amount
          }
          className="bg-[#3B0069] text-white px-10 py-2 rounded-[12px] font-medium hover:bg-purple-800 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? "Saving..." : "Save"}
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            {isLoading ? (
              <Loader className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Check className="w-4 h-4 text-white" />
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

export default WorkerPayment;